// Mind2Magic CRM — WhatsApp Conversation View
// Renders WhatsApp messages in lead detail panel.
// Phase 1: Read-only (incoming messages from Twilio webhook).
// Includes: Unmatched messages panel (floating badge + slide-out).

import { store } from './store.js'

// ========== LEAD PANEL — WhatsApp Section ==========

// Track collapsed/expanded state per lead (default: collapsed)
const _waCollapsedState = {};
// Track "show all" state per lead (default: false, show only last 3)
const _waShowAllState = {};

function renderWhatsAppSection(lead) {
    if (!lead) return '';
    const supaId = lead._supabaseId || lead.id;
    if (!supaId || (typeof supaId === 'string' && supaId.length !== 36)) return '';

    const messages = store.whatsappMessages[supaId];
    const hasMessages = messages && messages.length > 0;
    const count = hasMessages ? messages.length : 0;
    const isLoading = store.whatsappMessages[supaId + '_loading'];

    // Default to collapsed
    if (_waCollapsedState[supaId] === undefined) _waCollapsedState[supaId] = true;
    const isCollapsed = _waCollapsedState[supaId];
    const chevron = isCollapsed ? '\u25B8' : '\u25BE';

    const showAll = _waShowAllState[supaId] || false;
    const cappedMessages = hasMessages && !showAll && messages.length > 3
        ? messages.slice(-3)
        : messages;
    const hasMore = hasMessages && messages.length > 3 && !showAll;

    return `
        <div class="panel-section" id="waSectionWrapper">
            <div class="panel-section-title" style="display:flex;justify-content:space-between;align-items:center;cursor:pointer;user-select:none;border-radius:6px;padding:6px 8px;transition:background 0.15s;" onclick="window.waToggleSection('${supaId}')" onmouseenter="this.style.background='var(--bg-2)'" onmouseleave="this.style.background='transparent'">
                <span>${chevron} WhatsApp (${count})</span>
                ${isLoading ? '<span style="font-size:11px;color:var(--text-3);">Cargando...</span>' : ''}
            </div>
            ${!isCollapsed ? `
                <div id="waMessagesContainer" style="max-height:400px;overflow-y:auto;padding:8px;${hasMessages ? '' : 'display:none;'}">
                    ${hasMore ? `<div style="text-align:center;margin-bottom:8px;"><a href="#" onclick="event.preventDefault();window.waShowAllMessages('${supaId}')" style="font-size:11px;color:var(--purple);text-decoration:none;cursor:pointer;">Ver todos (${messages.length})</a></div>` : ''}
                    ${hasMessages ? renderWhatsAppMessages(cappedMessages) : ''}
                </div>
                ${!hasMessages && !isLoading ? '<div style="font-size:12px;color:var(--text-3);text-align:center;padding:12px;">Sin mensajes de WhatsApp</div>' : ''}
            ` : ''}
        </div>
    `;
}

function waToggleSection(supaId) {
    _waCollapsedState[supaId] = !_waCollapsedState[supaId];
    // Re-render the lead panel WhatsApp section
    _reRenderWaSection(supaId);
}

function waShowAllMessages(supaId) {
    _waShowAllState[supaId] = true;
    _reRenderWaSection(supaId);
}

function _reRenderWaSection(supaId) {
    // Find the current lead that matches this supaId
    const lead = [...store.leads, ...store.historico].find(l => (l._supabaseId || l.id) === supaId);
    if (!lead) return;
    const section = document.getElementById('waSectionWrapper');
    if (section) {
        section.outerHTML = renderWhatsAppSection(lead);
    }
}

function renderWhatsAppMessages(messages) {
    if (!messages || messages.length === 0) return '';

    let lastDate = '';
    return messages.map(msg => {
        const isIncoming = msg.direction === 'in';
        const dt = new Date(msg.created_at);
        const dateStr = dt.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
        const timeStr = dt.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });

        // Date separator
        let dateSep = '';
        if (dateStr !== lastDate) {
            lastDate = dateStr;
            dateSep = `<div style="text-align:center;margin:12px 0 8px;"><span style="font-size:10px;background:var(--bg-3);color:var(--text-3);padding:2px 10px;border-radius:8px;">${dateStr}</span></div>`;
        }

        const bubbleBg = isIncoming
            ? 'background:var(--bg-2);border-radius:12px 12px 12px 4px;'
            : 'background:var(--purple);color:white;border-radius:12px 12px 4px 12px;';
        const align = isIncoming ? '' : 'text-align:right;';
        const margin = isIncoming ? 'margin-right:40px;' : 'margin-left:40px;';
        const timeColor = isIncoming ? 'color:var(--text-3);' : 'color:rgba(255,255,255,0.7);';

        const content = window.escHtml ? window.escHtml(msg.content || '') : (msg.content || '');

        return `${dateSep}<div style="margin-bottom:6px;${align}${margin}">
            <div style="${bubbleBg}padding:8px 12px;display:inline-block;max-width:85%;text-align:left;">
                <div style="font-size:13px;word-break:break-word;line-height:1.4;">${content}</div>
                <div style="font-size:10px;${timeColor}margin-top:3px;text-align:right;">${timeStr}</div>
            </div>
        </div>`;
    }).join('');
}

async function loadWhatsAppMessages(leadSupabaseId) {
    if (!leadSupabaseId || store.whatsappMessages[leadSupabaseId + '_loading']) return;

    store.whatsappMessages[leadSupabaseId + '_loading'] = true;
    try {
        const messages = await window.supabaseDB.fetchWhatsAppMessages(leadSupabaseId);
        delete store.whatsappMessages[leadSupabaseId + '_loading'];
        if (messages && messages.length > 0) {
            store.whatsappMessages[leadSupabaseId] = messages;
            // Re-render if panel still open for this lead
            _reRenderWaSection(leadSupabaseId);
        }
    } catch (e) {
        delete store.whatsappMessages[leadSupabaseId + '_loading'];
        console.error('loadWhatsAppMessages error:', e);
    }
}


// ========== UNMATCHED MESSAGES — Floating Badge + Panel ==========

// Group unmatched messages by phone number
function _groupByPhone(messages) {
    const groups = {};
    for (const msg of messages) {
        const phone = msg.phone_number || 'unknown';
        if (!groups[phone]) groups[phone] = [];
        groups[phone].push(msg);
    }
    // Sort groups by most recent message (newest first)
    return Object.entries(groups)
        .map(([phone, msgs]) => ({
            phone,
            messages: msgs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
            lastMessage: msgs[0],
            count: msgs.length
        }))
        .sort((a, b) => new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at));
}

// Create or update the floating WhatsApp badge
function updateWhatsAppBadge() {
    const count = store.unmatchedWaMessages.length;
    let badge = document.getElementById('waBadge');

    if (count === 0) {
        if (badge) badge.style.display = 'none';
        return;
    }

    if (!badge) {
        badge = document.createElement('div');
        badge.id = 'waBadge';
        badge.className = 'wa-floating-badge';
        badge.onclick = () => openWhatsAppPanel();
        badge.innerHTML = `
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <span class="wa-badge-count">${count}</span>
        `;
        document.body.appendChild(badge);
    } else {
        badge.style.display = 'flex';
        badge.querySelector('.wa-badge-count').textContent = count;
    }
}

// Open the unmatched messages panel
function openWhatsAppPanel() {
    let panel = document.getElementById('waUnmatchedPanel');
    if (!panel) {
        panel = document.createElement('div');
        panel.id = 'waUnmatchedPanel';
        panel.className = 'panel';
        panel.innerHTML = `
            <div class="panel-head">
                <h2 class="panel-title">WhatsApp sin vincular</h2>
                <div class="panel-actions">
                    <button class="btn btn-ghost" onclick="window.closeWhatsAppPanel()" style="font-size:18px;padding:4px 8px;">&#10005;</button>
                </div>
            </div>
            <div class="panel-body" id="waUnmatchedBody"></div>
        `;
        document.body.appendChild(panel);
    }

    // Load unmatched from Supabase if empty
    if (store.unmatchedWaMessages.length === 0) {
        _loadUnmatchedMessages().then(() => _renderUnmatchedPanel());
    } else {
        _renderUnmatchedPanel();
    }

    // Close lead panel if open (avoid overlap)
    document.getElementById('panel')?.classList.remove('active');
    panel.classList.add('active');
}

function closeWhatsAppPanel() {
    document.getElementById('waUnmatchedPanel')?.classList.remove('active');
}

async function _loadUnmatchedMessages() {
    const messages = await window.supabaseDB?.fetchUnmatchedMessages();
    if (messages && messages.length > 0) {
        // Dedup
        for (const msg of messages) {
            if (!store.unmatchedWaMessages.some(m => m.id === msg.id)) {
                store.unmatchedWaMessages.push(msg);
            }
        }
        updateWhatsAppBadge();
    }
}

function _renderUnmatchedPanel() {
    const body = document.getElementById('waUnmatchedBody');
    if (!body) return;

    const groups = _groupByPhone(store.unmatchedWaMessages);

    if (groups.length === 0) {
        body.innerHTML = '<div style="text-align:center;padding:40px 20px;color:var(--text-3);font-size:13px;">No hay mensajes sin vincular</div>';
        return;
    }

    body.innerHTML = groups.map(g => {
        const esc = window.escHtml || (s => s);
        const preview = esc((g.lastMessage.content || '').substring(0, 80));
        const timeAgo = _timeAgo(g.lastMessage.created_at);

        return `
            <div class="wa-unmatched-group" data-phone="${esc(g.phone)}">
                <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
                    <div>
                        <div style="font-size:14px;font-weight:600;color:var(--text-1);">${esc(g.phone)}</div>
                        <div style="font-size:11px;color:var(--text-3);">${g.count} mensaje${g.count > 1 ? 's' : ''} &middot; ${timeAgo}</div>
                    </div>
                </div>
                <div style="font-size:12px;color:var(--text-2);margin-bottom:10px;line-height:1.4;">${preview}${g.lastMessage.content && g.lastMessage.content.length > 80 ? '...' : ''}</div>
                <div id="waConvo_${g.phone.replace(/[^0-9]/g, '')}" style="display:none;max-height:200px;overflow-y:auto;margin-bottom:10px;padding:4px;">
                </div>
                <div style="display:flex;gap:8px;flex-wrap:wrap;">
                    <button class="btn btn-primary" style="font-size:11px;padding:5px 12px;" onclick="window.waCreateLead('${esc(g.phone)}')">Crear Lead</button>
                    <button class="btn" style="font-size:11px;padding:5px 12px;background:#F97316;color:white;border:none;border-radius:6px;" onclick="window.waOpenQuoteModal('${esc(g.phone)}')">Cotización Enviada</button>
                    <button class="btn btn-ghost" style="font-size:11px;padding:5px 12px;" onclick="window.waLinkToLead('${esc(g.phone)}')">Vincular a Lead</button>
                    <button class="btn btn-ghost" style="font-size:11px;padding:5px 10px;" onclick="window.waToggleConvo('${esc(g.phone)}')">Ver mensajes</button>
                </div>
            </div>
        `;
    }).join('');
}

function _timeAgo(dateStr) {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'ahora';
    if (mins < 60) return `hace ${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `hace ${hours}h`;
    const days = Math.floor(hours / 24);
    return `hace ${days}d`;
}

// Toggle conversation view for a phone group
function waToggleConvo(phone) {
    const safeId = phone.replace(/[^0-9]/g, '');
    const container = document.getElementById('waConvo_' + safeId);
    if (!container) return;

    if (container.style.display !== 'none') {
        container.style.display = 'none';
        return;
    }

    const msgs = store.unmatchedWaMessages
        .filter(m => m.phone_number === phone)
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    container.innerHTML = renderWhatsAppMessages(msgs);
    container.style.display = 'block';
    container.scrollTop = container.scrollHeight;
}

// Create a new lead from unmatched phone number
function waCreateLead(phone) {
    // Pre-fill the new lead modal with the phone number
    window.openModal('newLeadModal');
    const phoneInput = document.getElementById('fTel');
    if (phoneInput) phoneInput.value = phone;

    // Store phone for post-save linking
    store._pendingWaPhone = phone;

    // Hook into saveLead to link messages after creation
    const origSaveLead = window.saveLead;
    window.saveLead = function() {
        origSaveLead();
        // After save, find the new lead and link messages
        const newLead = store.leads[store.leads.length - 1];
        if (newLead && store._pendingWaPhone) {
            _linkPhoneToLead(store._pendingWaPhone, newLead);
            delete store._pendingWaPhone;
        }
        // Restore original
        window.saveLead = origSaveLead;
    };
}

// Show lead search for linking
function waLinkToLead(phone) {
    const allLeads = [...store.leads, ...store.historico];
    // Build a small search modal
    const modalId = 'waLinkModal';
    let modal = document.getElementById(modalId);
    if (modal) modal.remove();

    modal = document.createElement('div');
    modal.id = modalId;
    modal.className = 'modal-bg active';
    modal.innerHTML = `
        <div class="modal" style="max-width:420px;">
            <div class="modal-head">
                <h2 class="modal-title">Vincular ${window.escHtml?.(phone) || phone} a lead</h2>
                <button class="modal-close" onclick="document.getElementById('${modalId}').remove()">&times;</button>
            </div>
            <div class="modal-body">
                <input type="text" class="form-input" id="waLinkSearch" placeholder="Buscar por nombre o telefono..." style="margin-bottom:12px;" oninput="window._waFilterLeads(this.value, '${phone}')">
                <div id="waLinkResults" style="max-height:300px;overflow-y:auto;"></div>
            </div>
        </div>
    `;
    document.body.appendChild(modal);

    // Show first 10 leads
    _waFilterLeads('', phone);
    document.getElementById('waLinkSearch')?.focus();
}

function _waFilterLeads(query, phone) {
    const container = document.getElementById('waLinkResults');
    if (!container) return;
    const esc = window.escHtml || (s => s);

    const allLeads = [...store.leads, ...store.historico];
    const q = query.toLowerCase().trim();
    const filtered = allLeads
        .filter(l => {
            if (!q) return true;
            return (l.titular || '').toLowerCase().includes(q)
                || (l.phone || '').includes(q)
                || (l.contacto || '').toLowerCase().includes(q);
        })
        .slice(0, 15);

    if (filtered.length === 0) {
        container.innerHTML = '<div style="padding:12px;color:var(--text-3);font-size:12px;text-align:center;">Sin resultados</div>';
        return;
    }

    container.innerHTML = filtered.map(l => `
        <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 10px;border-bottom:1px solid var(--border);cursor:pointer;" onclick="window._waConfirmLink('${esc(phone)}', '${l.id}')">
            <div>
                <div style="font-size:13px;font-weight:500;color:var(--text-1);">${esc(l.titular || 'Sin nombre')}</div>
                <div style="font-size:11px;color:var(--text-3);">${esc(l.phone || '')} &middot; ${esc(l.type || '')} &middot; ${esc(l.stage || '')}</div>
            </div>
            <span style="font-size:11px;color:var(--purple);">Vincular</span>
        </div>
    `).join('');
}

async function _waConfirmLink(phone, leadId) {
    const lead = [...store.leads, ...store.historico].find(l => l.id === leadId);
    if (!lead) return;

    await _linkPhoneToLead(phone, lead);

    // Save phone number on lead so backend auto-matches future messages
    if (!lead.phone || lead.phone !== phone) {
        lead.phone = phone;
        window.syncLeadToSupabase?.(lead);
    }

    // Close link modal
    document.getElementById('waLinkModal')?.remove();

    window.toast?.(`Mensajes de ${phone} vinculados a ${lead.titular}`, 'success');
}

// Link all messages from a phone number to a lead
async function _linkPhoneToLead(phone, lead) {
    const supaId = lead._supabaseId || lead.id;
    if (!supaId || (typeof supaId === 'string' && supaId.length !== 36)) {
        // Lead doesn't have Supabase ID yet — wait a bit and retry
        setTimeout(() => _linkPhoneToLead(phone, lead), 2000);
        return;
    }

    const msgsToLink = store.unmatchedWaMessages.filter(m => m.phone_number === phone);

    // Link in Supabase (bulk — one call per message)
    for (const msg of msgsToLink) {
        try {
            await window.supabaseDB?.linkMessageToLead(msg.id, supaId);
        } catch (err) {
            console.error(`Failed to link message ${msg.id} to lead ${supaId}:`, err);
        }
    }

    // Move to matched cache
    if (!store.whatsappMessages[supaId]) store.whatsappMessages[supaId] = [];
    for (const msg of msgsToLink) {
        msg.lead_id = supaId;
        if (!store.whatsappMessages[supaId].some(m => m.id === msg.id)) {
            store.whatsappMessages[supaId].push(msg);
        }
    }
    store.whatsappMessages[supaId].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    // Remove from unmatched
    store.unmatchedWaMessages = store.unmatchedWaMessages.filter(m => m.phone_number !== phone);

    // Update badge + panel
    updateWhatsAppBadge();
    updateWhatsAppInboxBadge();
    _renderUnmatchedPanel();

    // Update summary for the linked lead
    const linkedMsgs = store.whatsappMessages[supaId] || [];
    if (linkedMsgs.length > 0) {
        const last = linkedMsgs[linkedMsgs.length - 1];
        store.waLeadSummaries[supaId] = {
            lastMessageAt: last.created_at,
            lastDirection: last.direction || 'in',
            messageCount: linkedMsgs.length
        };
    }

    // If lead panel is open, refresh WhatsApp section
    if (lead.id === store.currentLeadId) {
        _reRenderWaSection(supaId);
    }
}

// Load unmatched on init (called after login)
async function loadUnmatchedOnInit() {
    await _loadUnmatchedMessages();
    // Auto-link any unmatched messages whose phone matches an existing lead
    await autoLinkUnmatched();
}

// ========== AUTO-LINK — Match unmatched phones to existing leads ==========

function _buildPhoneToLeadMap() {
    const map = {};
    const allLeads = [...store.leads, ...store.historico];
    for (const lead of allLeads) {
        if (!lead.phone) continue;
        const supaId = lead._supabaseId || lead.id;
        if (!supaId || (typeof supaId === 'string' && supaId.length !== 36)) continue;
        // Store under multiple variants for robust matching
        const raw = lead.phone;
        const digits = raw.replace(/\D/g, '');
        map[raw] = lead;
        if (digits) {
            map[digits] = lead;
            map['+' + digits] = lead;
            // Colombian: last 10 digits with +57
            if (digits.length >= 10) {
                map['+57' + digits.slice(-10)] = lead;
                map[digits.slice(-10)] = lead;
            }
        }
    }
    return map;
}

async function autoLinkUnmatched() {
    if (store.unmatchedWaMessages.length === 0) return;
    const phoneMap = _buildPhoneToLeadMap();
    if (Object.keys(phoneMap).length === 0) return;

    // Get unique phones from unmatched messages
    const phones = [...new Set(store.unmatchedWaMessages.map(m => m.phone_number))];
    let linked = 0;

    for (const phone of phones) {
        const digits = phone.replace(/\D/g, '');
        const lead = phoneMap[phone]
            || phoneMap[digits]
            || phoneMap['+' + digits]
            || (digits.length >= 10 ? phoneMap['+57' + digits.slice(-10)] : null)
            || (digits.length >= 10 ? phoneMap[digits.slice(-10)] : null);

        if (lead) {
            await _linkPhoneToLead(phone, lead);
            linked++;
            window.toast?.(`Auto-vinculado: ${phone} → ${lead.titular}`, 'success');
        }
    }
    if (linked > 0) {
        console.log(`[WhatsApp] Auto-linked ${linked} phone(s) to existing leads`);
    }
}

// Called when a new unmatched message arrives via realtime
async function autoLinkSinglePhone(phone) {
    const phoneMap = _buildPhoneToLeadMap();
    const digits = phone.replace(/\D/g, '');
    const lead = phoneMap[phone]
        || phoneMap[digits]
        || phoneMap['+' + digits]
        || (digits.length >= 10 ? phoneMap['+57' + digits.slice(-10)] : null)
        || (digits.length >= 10 ? phoneMap[digits.slice(-10)] : null);

    if (lead) {
        await _linkPhoneToLead(phone, lead);
        window.toast?.(`Auto-vinculado: ${phone} → ${lead.titular}`, 'success');
        return true;
    }
    return false;
}

// Called when a lead is created/updated — check if any unmatched messages match its phone
async function autoLinkForLead(lead) {
    if (!lead.phone || store.unmatchedWaMessages.length === 0) return;
    const supaId = lead._supabaseId || lead.id;
    if (!supaId || (typeof supaId === 'string' && supaId.length !== 36)) return;

    const leadDigits = lead.phone.replace(/\D/g, '');
    const phones = [...new Set(store.unmatchedWaMessages.map(m => m.phone_number))];

    for (const phone of phones) {
        const msgDigits = phone.replace(/\D/g, '');
        // Match if last 10 digits are the same (handles country code differences)
        const match = (leadDigits === msgDigits)
            || (leadDigits.length >= 10 && msgDigits.length >= 10 && leadDigits.slice(-10) === msgDigits.slice(-10));

        if (match) {
            await _linkPhoneToLead(phone, lead);
            window.toast?.(`Auto-vinculado: ${phone} → ${lead.titular}`, 'success');
        }
    }
}


// ========== COTIZACIÓN ENVIADA — Quick Lead from WhatsApp Quote ==========

const _MONTHS_ES = { enero:1, febrero:2, marzo:3, abril:4, mayo:5, junio:6, julio:7, agosto:8, septiembre:9, octubre:10, noviembre:11, diciembre:12 };
const _TYPE_LABELS = { disney:'Disney', universal:'Universal', disney_universal:'Disney + Universal', crucero:'Crucero', parques_crucero:'Parques + Crucero', hotel:'Hotel', evento:'Evento', otro:'Otro' };

function parseQuoteText(text) {
    const r = {
        type: null, destination: null, travelDate: null, travelEnd: null,
        ship: null, cruiseLine: null, departurePort: null, nights: null,
        adultos: 0, menores: 0, cabins: 0, viajeros: null,
        totalMin: null, totalMax: null, parkDays: null, hotelName: null,
        cruiseData: null,
    };
    if (!text) return r;

    // Type detection
    if (/camarote|crucero|cruise|naviera/i.test(text)) r.type = 'crucero';
    else if (/disney.*universal|universal.*disney/i.test(text)) r.type = 'disney_universal';
    else if (/disney/i.test(text)) r.type = 'disney';
    else if (/universal/i.test(text)) r.type = 'universal';
    else if (/hotel|resort/i.test(text)) r.type = 'hotel';

    // Dates: "DD al DD de MES de YYYY"
    const dm1 = text.match(/(\d{1,2})\s+al\s+(\d{1,2})\s+de\s+(\w+)\s+de\s+(\d{4})/i);
    if (dm1) {
        const mo = _MONTHS_ES[dm1[3].toLowerCase()];
        if (mo) {
            r.travelDate = `${dm1[4]}-${String(mo).padStart(2,'0')}-${String(dm1[1]).padStart(2,'0')}`;
            r.travelEnd = `${dm1[4]}-${String(mo).padStart(2,'0')}-${String(dm1[2]).padStart(2,'0')}`;
        }
    }
    // Cross-month: "DD de MES al DD de MES de YYYY"
    if (!r.travelDate) {
        const dm2 = text.match(/(\d{1,2})\s+de\s+(\w+)\s+al\s+(\d{1,2})\s+de\s+(\w+)\s+de\s+(\d{4})/i);
        if (dm2) {
            const m1 = _MONTHS_ES[dm2[2].toLowerCase()], m2 = _MONTHS_ES[dm2[4].toLowerCase()];
            if (m1 && m2) {
                const y2 = m2 < m1 ? parseInt(dm2[5]) + 1 : parseInt(dm2[5]);
                r.travelDate = `${dm2[5]}-${String(m1).padStart(2,'0')}-${String(dm2[1]).padStart(2,'0')}`;
                r.travelEnd = `${y2}-${String(m2).padStart(2,'0')}-${String(dm2[3]).padStart(2,'0')}`;
            }
        }
    }

    // Travelers
    const tm = text.match(/(\d+)\s*adultos?\s*\+?\s*(\d+)\s*menores?/i);
    if (tm) { r.adultos = parseInt(tm[1]); r.menores = parseInt(tm[2]); }
    else {
        const a = text.match(/(\d+)\s*adultos?/i); if (a) r.adultos = parseInt(a[1]);
        const m = text.match(/(\d+)\s*menores?/i); if (m) r.menores = parseInt(m[1]);
    }
    // Minor ages: "(9 y 4 años)" or "(9, 4 y 2 años)" — require "año" suffix to avoid "(1 PARQUE POR DÍA)"
    const ageMatch = text.match(/\((\d+(?:\s*[,y]\s*\d+)*)\s*años?\)/i);
    r.edadesMenores = ageMatch ? ageMatch[1].split(/\s*[,y]\s*/).map(s => s.trim()).filter(Boolean) : [];

    const cm = text.match(/(\d+)\s*camarotes?/i);
    if (cm) r.cabins = parseInt(cm[1]);
    const parts = [];
    if (r.adultos) parts.push(`${r.adultos} adulto${r.adultos > 1 ? 's' : ''}`);
    if (r.menores) {
        parts.push(`${r.menores} menor${r.menores > 1 ? 'es' : ''}`);
        if (r.edadesMenores.length) parts[parts.length - 1] += ` (${r.edadesMenores.join(', ')} años)`;
    }
    r.viajeros = parts.join(' + ') || null;

    // Ship: "SHIP – N NOCHES DEST" or "SHIP - N NOCHES"
    const sm = text.match(/(?:[⛴️🚢]\s*)?([A-Z][A-Za-zÀ-ÿ ]+?)\s*[–-]\s*(\d+)\s*NOCHES?\s+([A-ZÀ-ÿ][A-Za-zÀ-ÿ ]+)/i);
    if (sm) { r.ship = sm[1].trim(); r.nights = parseInt(sm[2]); r.destination = sm[3].trim(); }

    // Cruise line: "LINE – SHIP"
    const lm = text.match(/Disney Cruise Line|Royal Caribbean|MSC Cruises?|Norwegian|Carnival|Celebrity|Virgin Voyages/i);
    if (lm) r.cruiseLine = lm[0];

    // Departure port
    const pm = text.match(/Sale de:\s*(.+?)(?:\s*[\u{1F1E6}-\u{1F1FF}]|\s*\n|$)/iu);
    if (pm) r.departurePort = pm[1].trim();

    // Parse USD amount helper
    function _parseUSD(s) {
        const lc = s.lastIndexOf(','), ld = s.lastIndexOf('.');
        if (lc > ld) s = s.replace(/\./g, '').replace(',', '.'); // LATAM: 18.325,50
        else s = s.replace(/,/g, ''); // US: 18,325.50 or 18,325
        return parseFloat(s);
    }

    // Search full text for USD amounts (incremental +X USD and add-ons filtered below)
    // Skip: add-on line items (Memory Maker, Genie+, etc.) and incremental prices (+483 USD)
    const addonRe = /memory\s*maker|genie\s*\+|express\s*pass|seguro|insurance|protection\s+plan|propina/i;
    const searchLines = text.split('\n');
    const amounts = [];
    for (let i = 0; i < searchLines.length; i++) {
        const line = searchLines[i];
        const usdMatches = [...line.matchAll(/(?:desde\s+)?(?:💰\s*)?([\d.,]+)\s*USD/gi)];
        if (!usdMatches.length) continue;
        // Check this line + 2 lines above for add-on keywords
        const ctx = [searchLines[i - 2], searchLines[i - 1], line].filter(Boolean).join(' ');
        if (addonRe.test(ctx)) continue;
        for (const m of usdMatches) {
            // Skip incremental prices like "+483.76 USD"
            const beforeAmt = line.substring(0, m.index);
            if (/\+\s*$/.test(beforeAmt)) continue;
            const v = _parseUSD(m[1]);
            if (!isNaN(v) && v > 100) amounts.push(v);
        }
    }
    if (amounts.length > 0) { r.totalMin = Math.min(...amounts); r.totalMax = Math.max(...amounts); }
    // Fallback: "Total: X USD" or "Total estimado: X USD"
    if (!r.totalMin) {
        const tf = text.match(/[Tt]otal(?:\s+estimado)?[:\s]+([\d.,]+)\s*USD/);
        if (tf) { const v = _parseUSD(tf[1]); if (!isNaN(v) && v > 100) r.totalMin = r.totalMax = v; }
    }

    // Nights fallback
    if (!r.nights) { const nm = text.match(/(\d+)\s*NOCHES?/i); if (nm) r.nights = parseInt(nm[1]); }

    // Park days
    const pd = text.match(/(\d+)\s*(?:d[ií]as?|days?)\s*(?:de\s*)?(?:parque|park|disney|universal)/i);
    if (pd) r.parkDays = parseInt(pd[1]);

    // Hotel name: "Hotel: X" or "Resort: X" or "🏨 Resort: X"
    const hm = text.match(/(?:Hotel|Resort)[:\s]+([A-ZÀ-ÿ][A-Za-zÀ-ÿ'''&\-. ]+?)(?:\s*[–|]|\s*desde|\s*\n)/i);
    if (hm) r.hotelName = hm[1].trim();

    // Build cruise data
    if (r.type === 'crucero' || r.type === 'parques_crucero') {
        r.cruiseData = {
            line: r.cruiseLine || '', ship: r.ship || '',
            departurePort: r.departurePort || '',
            departureDate: r.travelDate || '', returnDate: r.travelEnd || '',
            cabin: '', cost: r.totalMin || 0, notes: '',
        };
    }

    return r;
}

function _extractProfileName(phone) {
    const msgs = store.unmatchedWaMessages.filter(m => m.phone_number === phone);
    for (const msg of msgs) {
        const rp = msg.raw_payload;
        if (!rp) continue;
        // Meta format (Chakra)
        const metaName = rp.contacts?.[0]?.profile?.name;
        if (metaName) return metaName;
        // Twilio format
        if (rp.ProfileName) return rp.ProfileName;
    }
    return '';
}

function waOpenQuoteModal(phone) {
    const modalId = 'waQuoteModal';
    let modal = document.getElementById(modalId);
    if (modal) modal.remove();

    const esc = window.escHtml || (s => s);
    const profileName = _extractProfileName(phone);

    modal = document.createElement('div');
    modal.id = modalId;
    modal.className = 'modal-bg active';
    modal.innerHTML = `
        <div class="modal" style="max-width:600px;">
            <div class="modal-head">
                <h2 class="modal-title">Cotización Enviada</h2>
                <button class="modal-close" onclick="document.getElementById('${modalId}').remove()">&times;</button>
            </div>
            <div class="modal-body">
                <div class="form-row">
                    <div class="form-group"><label class="form-label">Nombre *</label><input type="text" class="form-input" id="waQuoteName" value="${esc(profileName)}" placeholder="Nombre del cliente"></div>
                    <div class="form-group"><label class="form-label">Teléfono</label><input type="text" class="form-input" id="waQuotePhone" value="${esc(phone)}" readonly style="opacity:0.7;"></div>
                </div>
                <div class="form-group" style="margin-top:12px;">
                    <label class="form-label">Pega la cotización aquí</label>
                    <textarea class="form-input" id="waQuoteText" rows="8" placeholder="Pega el texto de la cotización que enviaste por WhatsApp..." style="font-size:12px;line-height:1.5;resize:vertical;" oninput="window._waParseAndPreview()"></textarea>
                </div>
                <div id="waQuotePreview" style="display:none;margin-top:12px;padding:14px;background:var(--bg-2);border-radius:8px;border:1px solid var(--border);"></div>
            </div>
            <div class="modal-foot">
                <button class="btn btn-secondary" onclick="document.getElementById('${modalId}').remove()">Cancelar</button>
                <button class="btn btn-primary" onclick="window._waCreateFromQuote('${esc(phone)}')">Crear Lead en Cotización</button>
            </div>
        </div>
    `;
    document.body.appendChild(modal);
    document.getElementById('waQuoteName')?.focus();
}

function _waParseAndPreview() {
    const text = document.getElementById('waQuoteText')?.value || '';
    const preview = document.getElementById('waQuotePreview');
    if (!preview) return;
    if (!text.trim()) { preview.style.display = 'none'; return; }

    const p = parseQuoteText(text);
    store._pendingQuoteParsed = p;
    const esc = window.escHtml || (s => s);

    const rows = [];
    if (p.type) rows.push(['Tipo', _TYPE_LABELS[p.type] || p.type]);
    if (p.cruiseLine) rows.push(['Naviera', p.cruiseLine]);
    if (p.ship) rows.push(['Barco', p.ship]);
    if (p.departurePort) rows.push(['Puerto', p.departurePort]);
    if (p.destination) rows.push(['Destino', p.destination]);
    if (p.nights) rows.push(['Noches', p.nights]);
    if (p.travelDate) rows.push(['Inicio', p.travelDate]);
    if (p.travelEnd) rows.push(['Fin', p.travelEnd]);
    if (p.viajeros) rows.push(['Viajeros', p.viajeros]);
    if (p.cabins) rows.push(['Camarotes', p.cabins]);
    if (p.parkDays) rows.push(['Días parque', p.parkDays]);
    if (p.hotelName) rows.push(['Hotel', p.hotelName]);
    if (p.totalMin) {
        const ps = p.totalMin === p.totalMax
            ? `$${p.totalMin.toLocaleString()} USD`
            : `$${p.totalMin.toLocaleString()} – $${p.totalMax.toLocaleString()} USD`;
        rows.push(['Rango precio', ps]);
    }

    if (rows.length === 0) {
        preview.innerHTML = '<div style="color:var(--text-3);font-size:12px;text-align:center;">No se detectaron datos estructurados. El lead se creará con el texto guardado.</div>';
    } else {
        preview.innerHTML = `
            <div style="font-size:12px;font-weight:600;color:var(--text-1);margin-bottom:10px;">Datos detectados:</div>
            <div style="display:grid;grid-template-columns:auto 1fr;gap:4px 12px;font-size:12px;">
                ${rows.map(([l, v]) => `<span style="color:var(--text-3);font-weight:500;">${esc(l)}</span><span style="color:var(--text-1);">${esc(String(v))}</span>`).join('')}
            </div>
        `;
    }
    preview.style.display = 'block';
}

function _waCreateFromQuote(phone) {
    const name = document.getElementById('waQuoteName')?.value?.trim();
    if (!name) {
        window.toast?.('Ingresa el nombre del cliente', 'error');
        document.getElementById('waQuoteName')?.focus();
        return;
    }

    const text = document.getElementById('waQuoteText')?.value || '';
    const parsed = store._pendingQuoteParsed || parseQuoteText(text);

    window.saveState?.('crear lead desde cotización');

    const normalizedPhone = window.normalizePhone?.(phone) || phone;
    const clientKey = normalizedPhone || ('name:' + name.toLowerCase());
    const { leads, clients, team } = store;

    if (!clients[clientKey]) {
        clients[clientKey] = {
            titular: name, contacto: name, phone: normalizedPhone, email: '',
            referido: '', canalAlerta: 'whatsapp', trips: [], createdAt: new Date()
        };
    }

    const tripId = 'trip_' + Date.now();
    const type = parsed.type || 'otro';
    const resp = store.settings?.responsableDefault || team[0]?.nombre || 'Sara';
    const destMap = { disney: 'Walt Disney World', universal: 'Universal Orlando', disney_universal: 'Disney World + Universal Orlando', hotel: parsed.hotelName || '' };
    const dest = parsed.destination || parsed.ship || destMap[type] || '';
    const platMap = { disney: 'Disney', universal: 'Universal', disney_universal: 'Disney / Universal' };
    const platform = parsed.cruiseLine || platMap[type] || '';

    const lead = {
        id: tripId, clientKey, titular: name, contacto: name,
        phone: normalizedPhone, email: '', type, stage: 'cotizacion',
        travelDate: parsed.travelDate || null, travelEnd: parsed.travelEnd || null,
        destination: dest, platform, viajeros: parsed.viajeros || '',
        hotel: parsed.hotelName || parsed.ship || '',
        totalVenta: parsed.totalMin || 0, commission: 0, commission70: 0,
        abono: 0, abonado: 0, fechaAbono: null, fechaPagoFinal: null,
        reservationNum: null, progress: 0, responsable: resp,
        source: 'WhatsApp', createdAt: new Date(),
        formData: {
            adultos: parsed.adultos || 0,
            menores: parsed.menores || 0,
            edadesMenores: parsed.edadesMenores || [],
        },
        observaciones: text ? `--- Cotización enviada ---\n${text}` : '',
    };

    // Structured cruise data
    if (parsed.cruiseData) {
        lead.cruises = [parsed.cruiseData];
        lead.formData.cruises = lead.cruises;
    }
    // Structured hotel
    if (parsed.hotelName) {
        lead.hotels = [{ name: parsed.hotelName, provider: '', reservationNum: '', checkIn: parsed.travelDate || '', checkOut: parsed.travelEnd || '', rooms: 1, type: '', cost: 0, notes: '' }];
        lead.formData.hotels = lead.hotels;
    }
    // Structured park entries
    if (parsed.parkDays) {
        const parkBrand = type === 'universal' ? 'Universal Orlando' : type === 'disney_universal' ? 'Disney World' : 'Disney World';
        lead.parkEntries = [{ park: parkBrand, days: parsed.parkDays, ticketType: '', validFrom: parsed.travelDate || '', validTo: parsed.travelEnd || '', reservationNum: '', cost: 0, diningPlanType: 'none' }];
        if (type === 'disney_universal') {
            lead.parkEntries.push({ park: 'Universal Orlando', days: 0, ticketType: '', validFrom: parsed.travelDate || '', validTo: parsed.travelEnd || '', reservationNum: '', cost: 0, diningPlanType: 'none' });
        }
        lead.formData.parkEntries = lead.parkEntries;
    }
    // Transfer nights/cabins
    if (parsed.nights) lead.formData.noches = parsed.nights;
    if (parsed.cabins) lead.formData.camarotes = parsed.cabins;
    // Store raw quote
    if (text) lead.formData.cotizacionTexto = text;

    clients[clientKey].trips.push({
        id: tripId, source: 'WhatsApp', type, destination: dest,
        travelDate: lead.travelDate, travelEnd: lead.travelEnd,
        totalVenta: parsed.totalMin || 0, commission: 0, commission70: 0,
        abono: 0, abonado: 0, stage: 'cotizacion', progress: 0, responsable: resp,
    });

    leads.push(lead);

    window.addHistorial?.(lead, 'creacion', `Lead creado desde cotización WhatsApp — ${_TYPE_LABELS[type] || type}, ${dest || 'sin destino'}`);
    window.syncClientToSupabase?.(clientKey, clients[clientKey]);
    window.syncLeadToSupabase?.(lead);

    // Link WhatsApp messages after Supabase ID is assigned
    store._pendingWaPhone = phone;
    setTimeout(() => {
        const savedLead = leads.find(l => l.id === tripId);
        if (savedLead) _linkPhoneToLead(phone, savedLead);
        delete store._pendingWaPhone;
    }, 2000);

    delete store._pendingQuoteParsed;
    document.getElementById('waQuoteModal')?.remove();

    window.renderAll?.();
    window.toast?.(`"${name}" creado en Cotización enviada`, 'success');
}

function checkColdConversations() {
    if (store.settings.autoTareaWhatsApp === false) return;
    const now = Date.now();
    const COLD_THRESHOLD = (store.settings.alertaWhatsApp || 2) * 24 * 3600000;
    for (const [leadId, summary] of Object.entries(store.waLeadSummaries)) {
        if (summary.lastDirection !== 'in') continue;
        const elapsed = now - new Date(summary.lastMessageAt).getTime();
        if (elapsed < COLD_THRESHOLD) continue;
        const lead = [...store.leads, ...store.historico].find(l => (l._supabaseId || l.id) === leadId);
        if (!lead) continue;
        if (['cancelado', 'historico', 'finalizado', 'no_califica', 'no_compro'].includes(lead.stage)) continue;
        const existingTarea = store.tareas.find(t => t.titulo?.startsWith('Seguimiento WhatsApp') && t.leadId === lead.id && t.estado === 'pendiente');
        if (existingTarea) continue;
        const dias = Math.floor(elapsed / 86400000);
        const tareaData = {
            id: 'tarea_wa_' + Date.now() + '_' + leadId.substring(0, 8),
            titulo: 'Seguimiento WhatsApp — ' + (lead.titular || 'Lead'),
            descripcion: 'Ultimo mensaje del cliente hace ' + dias + ' dias. Pendiente respuesta.',
            responsable: lead.responsable || store.team[0]?.nombre || 'Sara',
            fechaLimite: new Date().toISOString().split('T')[0],
            leadId: lead.id,
            prioridad: 'alta',
            estado: 'pendiente',
            creadoPor: 'Sistema',
            autoType: 'wa_followup',
            fechaCreacion: new Date().toISOString()
        };
        store.tareas.push(tareaData);
        window.saveTareas?.(tareaData);
    }
}

// ========== WHATSAPP INBOX VIEW ==========

let _waSearchQuery = '';

function _renderQuoteSearchBar() {
    const esc = window.escHtml || (s => s);
    return `
        <div style="display:flex;gap:8px;align-items:center;padding:8px 16px;border-bottom:1px solid var(--border);">
            <input type="text" class="form-input" id="waQuoteSearchPhone" placeholder="Buscar por nombre o teléfono..." value="${esc(_waSearchQuery)}" style="flex:1;font-size:13px;padding:6px 10px;margin:0;" oninput="window.waInboxSearch(this.value)">
            <button class="btn" style="font-size:11px;padding:6px 14px;background:#F97316;color:white;border:none;border-radius:6px;white-space:nowrap;" onclick="window.waQuoteFromSearch()">Cotización Enviada</button>
        </div>
    `;
}

function waInboxSearch(query) {
    _waSearchQuery = query;
    const q = (query || '').trim().toLowerCase();
    const rows = document.querySelectorAll('[data-wa-search]');
    let shown = 0;
    for (const row of rows) {
        const match = !q || row.dataset.waSearch.toLowerCase().includes(q);
        row.style.display = match ? '' : 'none';
        if (match) shown++;
    }
    // Show/hide empty state
    const emptyEl = document.getElementById('waSearchEmpty');
    if (emptyEl) {
        emptyEl.style.display = (q && shown === 0) ? 'block' : 'none';
    }
}

function waQuoteFromSearch() {
    const input = document.getElementById('waQuoteSearchPhone');
    const phone = input?.value?.trim();
    if (!phone) { window.toast?.('Ingresa un número de teléfono', 'error'); input?.focus(); return; }
    waOpenQuoteModal(phone);
}

let _waInboxTab = 'pendientes'; // 'pendientes' | 'frias' | 'sinvincular' | 'recientes'

function renderWhatsAppInbox() {
    const container = document.getElementById('whatsappContent');
    if (!container) return;

    const allLeads = [...store.leads, ...store.historico];
    const now = Date.now();
    const coldDays = (store.settings.alertaWhatsApp || 2) * 24 * 3600000;

    // Build conversation list from summaries
    const conversations = [];
    for (const [leadId, summary] of Object.entries(store.waLeadSummaries)) {
        const lead = allLeads.find(l => (l._supabaseId || l.id) === leadId);
        if (!lead) continue;
        if (['cancelado', 'historico', 'finalizado', 'no_califica', 'no_compro'].includes(lead.stage)) continue;

        const elapsed = now - new Date(summary.lastMessageAt).getTime();
        const isPending = summary.lastDirection === 'in';
        const isCold = isPending && elapsed > coldDays;

        conversations.push({
            lead,
            leadId,
            summary,
            isPending,
            isCold,
            elapsed,
            lastAt: new Date(summary.lastMessageAt)
        });
    }

    // Sort by most recent first
    conversations.sort((a, b) => b.lastAt - a.lastAt);

    // Filter by tab
    let filtered;
    let emptyMsg;
    switch (_waInboxTab) {
        case 'pendientes':
            filtered = conversations.filter(c => c.isPending);
            filtered.sort((a, b) => b.elapsed - a.elapsed); // Oldest first = most urgent
            emptyMsg = 'No hay conversaciones pendientes de respuesta';
            break;
        case 'frias':
            filtered = conversations.filter(c => c.isCold);
            filtered.sort((a, b) => b.elapsed - a.elapsed);
            emptyMsg = 'No hay conversaciones frias';
            break;
        case 'sinvincular':
            // Render unmatched panel content
            _renderInboxUnmatched(container);
            return;
        case 'recientes':
        default:
            filtered = conversations;
            emptyMsg = 'No hay conversaciones de WhatsApp';
            break;
    }

    // Count for tabs
    const pendingCount = conversations.filter(c => c.isPending).length;
    const coldCount = conversations.filter(c => c.isCold).length;
    const unmatchedCount = store.unmatchedWaMessages.length;
    const unmatchedPhones = [...new Set(store.unmatchedWaMessages.map(m => m.phone_number))].length;

    container.innerHTML = `
        ${_renderInboxTabs(pendingCount, coldCount, unmatchedPhones, conversations.length)}
        ${_renderQuoteSearchBar()}
        <div id="waInboxList" style="padding:0 16px 16px;">
            ${filtered.length === 0
                ? `<div style="text-align:center;padding:40px 20px;color:var(--text-3);font-size:13px;">${emptyMsg}</div>`
                : filtered.map(c => _renderInboxRow(c)).join('')
            }
            <div id="waSearchEmpty" style="display:none;text-align:center;padding:40px 20px;color:var(--text-3);font-size:13px;">Sin resultados para la búsqueda</div>
        </div>
    `;
}

function _renderInboxTabs(pendingCount, coldCount, unmatchedCount, totalCount) {
    const esc = window.escHtml || (s => s);
    const tabs = [
        { id: 'pendientes', label: 'Pendientes', count: pendingCount, color: 'var(--orange, #F59E0B)' },
        { id: 'frias', label: 'Frias', count: coldCount, color: 'var(--red, #EF4444)' },
        { id: 'sinvincular', label: 'Sin vincular', count: unmatchedCount, color: '#1FA855' },
        { id: 'recientes', label: 'Recientes', count: totalCount, color: 'var(--text-3)' },
    ];

    return `<div style="display:flex;gap:4px;padding:16px 16px 12px;border-bottom:1px solid var(--border);overflow-x:auto;">
        ${tabs.map(t => `
            <button class="period-tab${_waInboxTab === t.id ? ' active' : ''}"
                onclick="window.setWaInboxTab('${t.id}')"
                style="white-space:nowrap;font-size:12px;padding:6px 12px;position:relative;">
                ${esc(t.label)}
                ${t.count > 0 ? `<span style="background:${t.color};color:white;font-size:10px;padding:1px 6px;border-radius:8px;margin-left:4px;font-weight:600;">${t.count}</span>` : ''}
            </button>
        `).join('')}
    </div>`;
}

function _renderInboxRow(c) {
    const esc = window.escHtml || (s => s);
    const lead = c.lead;
    const timeAgo = _timeAgo(c.summary.lastMessageAt);
    const name = esc(lead.titular || 'Sin nombre');
    const phone = esc(lead.phone || '');
    const stage = esc(lead.stage || '');
    const count = c.summary.messageCount || 0;

    // Status indicator
    let statusDot, statusLabel;
    if (c.isCold) {
        statusDot = 'background:var(--red, #EF4444);';
        statusLabel = `Fria (${Math.floor(c.elapsed / 86400000)}d)`;
    } else if (c.isPending) {
        statusDot = 'background:var(--orange, #F59E0B);';
        statusLabel = 'Pendiente respuesta';
    } else {
        statusDot = 'background:#1FA855;';
        statusLabel = 'Respondido';
    }

    // Get last message preview from cached messages
    const msgs = store.whatsappMessages[c.leadId];
    const lastMsg = msgs && msgs.length > 0 ? msgs[msgs.length - 1] : null;
    const preview = lastMsg ? esc((lastMsg.content || '').substring(0, 70)) : '';
    const previewSuffix = lastMsg && lastMsg.content && lastMsg.content.length > 70 ? '...' : '';
    const dirArrow = lastMsg?.direction === 'out' ? '<span style="color:var(--text-3);margin-right:2px;">Tu:</span>' : '';

    const searchData = esc(`${lead.titular || ''} ${lead.phone || ''} ${lead.contacto || ''}`);
    return `
        <div data-wa-search="${searchData}" style="display:flex;align-items:center;gap:12px;padding:12px 0;border-bottom:1px solid var(--border);cursor:pointer;transition:background 0.15s;"
             onclick="window.openLead('${lead.id}')"
             onmouseenter="this.style.background='var(--bg-2)'" onmouseleave="this.style.background='transparent'">
            <div style="flex-shrink:0;">
                <div style="width:8px;height:8px;border-radius:50%;${statusDot}"></div>
            </div>
            <div style="flex:1;min-width:0;">
                <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:2px;">
                    <span style="font-size:13px;font-weight:600;color:var(--text-1);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${name}</span>
                    <span style="font-size:10px;color:var(--text-3);white-space:nowrap;margin-left:8px;">${timeAgo}</span>
                </div>
                <div style="font-size:11px;color:var(--text-3);margin-bottom:3px;">${phone}${stage ? ' · ' + stage : ''} · ${count} msg</div>
                ${preview ? `<div style="font-size:12px;color:var(--text-2);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${dirArrow}${preview}${previewSuffix}</div>` : ''}
            </div>
            <div style="flex-shrink:0;display:flex;flex-direction:column;align-items:flex-end;gap:4px;">
                <span style="font-size:10px;color:var(--text-3);background:var(--bg-3);padding:2px 8px;border-radius:8px;">${esc(statusLabel)}</span>
                ${c.isPending ? `<button class="btn btn-ghost" style="font-size:10px;padding:2px 8px;color:#1FA855;border:1px solid #1FA855;border-radius:6px;" onclick="event.stopPropagation();window.waMarkResponded('${c.leadId}')">Respondido</button>` : ''}
            </div>
        </div>
    `;
}

function _renderInboxUnmatched(container) {
    const groups = _groupByPhone(store.unmatchedWaMessages);
    const allConversations = Object.entries(store.waLeadSummaries);
    const pendingCount = allConversations.filter(([, s]) => s.lastDirection === 'in').length;
    const coldDays = (store.settings.alertaWhatsApp || 2) * 24 * 3600000;
    const coldCount = allConversations.filter(([, s]) => s.lastDirection === 'in' && (Date.now() - new Date(s.lastMessageAt).getTime()) > coldDays).length;
    const unmatchedPhones = groups.length;

    container.innerHTML = `
        ${_renderInboxTabs(pendingCount, coldCount, unmatchedPhones, allConversations.length)}
        ${_renderQuoteSearchBar()}
        <div style="padding:0 16px 16px;">
            ${groups.length === 0
                ? '<div style="text-align:center;padding:40px 20px;color:var(--text-3);font-size:13px;">No hay mensajes sin vincular</div>'
                : groups.map(g => {
                    const esc = window.escHtml || (s => s);
                    const preview = esc((g.lastMessage.content || '').substring(0, 80));
                    const timeAgo = _timeAgo(g.lastMessage.created_at);
                    return `
                        <div data-wa-search="${esc(g.phone)}" style="padding:14px 0;border-bottom:1px solid var(--border);">
                            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;">
                                <div>
                                    <div style="font-size:14px;font-weight:600;color:var(--text-1);">${esc(g.phone)}</div>
                                    <div style="font-size:11px;color:var(--text-3);">${g.count} mensaje${g.count > 1 ? 's' : ''} · ${timeAgo}</div>
                                </div>
                            </div>
                            <div style="font-size:12px;color:var(--text-2);margin-bottom:10px;line-height:1.4;">${preview}${g.lastMessage.content && g.lastMessage.content.length > 80 ? '...' : ''}</div>
                            <div id="waInboxConvo_${g.phone.replace(/[^0-9]/g, '')}" style="display:none;max-height:200px;overflow-y:auto;margin-bottom:10px;padding:4px;"></div>
                            <div style="display:flex;gap:8px;flex-wrap:wrap;">
                                <button class="btn btn-primary" style="font-size:11px;padding:5px 12px;" onclick="window.waCreateLead('${esc(g.phone)}')">Crear Lead</button>
                                <button class="btn" style="font-size:11px;padding:5px 12px;background:#F97316;color:white;border:none;border-radius:6px;" onclick="window.waOpenQuoteModal('${esc(g.phone)}')">Cotización Enviada</button>
                                <button class="btn btn-ghost" style="font-size:11px;padding:5px 12px;" onclick="window.waLinkToLead('${esc(g.phone)}')">Vincular a Lead</button>
                                <button class="btn btn-ghost" style="font-size:11px;padding:5px 10px;" onclick="window.waInboxToggleConvo('${esc(g.phone)}')">Ver mensajes</button>
                            </div>
                        </div>`;
                }).join('')
            }
            <div id="waSearchEmpty" style="display:none;text-align:center;padding:40px 20px;color:var(--text-3);font-size:13px;">Sin resultados para la búsqueda</div>
        </div>
    `;
}

function setWaInboxTab(tab) {
    _waInboxTab = tab;
    renderWhatsAppInbox();
    // Re-apply active search after re-render
    if (_waSearchQuery) waInboxSearch(_waSearchQuery);
}

function waInboxToggleConvo(phone) {
    const safeId = phone.replace(/[^0-9]/g, '');
    const container = document.getElementById('waInboxConvo_' + safeId);
    if (!container) return;

    if (container.style.display !== 'none') {
        container.style.display = 'none';
        return;
    }

    const msgs = store.unmatchedWaMessages
        .filter(m => m.phone_number === phone)
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    container.innerHTML = renderWhatsAppMessages(msgs);
    container.style.display = 'block';
    container.scrollTop = container.scrollHeight;
}

// Mark a conversation as responded (manual — for replies sent via WhatsApp Web)
function waMarkResponded(leadId) {
    const summary = store.waLeadSummaries[leadId];
    if (!summary) return;
    summary.lastDirection = 'out';
    summary.lastMessageAt = new Date().toISOString();
    // Re-render inbox + badge
    renderWhatsAppInbox();
    updateWhatsAppInboxBadge();
    // Re-render pipeline card if visible
    window.renderPipeline?.();
    const lead = [...store.leads, ...store.historico].find(l => (l._supabaseId || l.id) === leadId);
    window.toast?.(`${lead?.titular || 'Lead'} marcado como respondido`, 'success');
}

// Update inbox badge count
function updateWhatsAppInboxBadge() {
    const badge = document.getElementById('badgeWhatsApp');
    if (!badge) return;
    // Count: pending responses + unmatched phones
    const pendingCount = Object.values(store.waLeadSummaries).filter(s => s.lastDirection === 'in').length;
    const unmatchedPhones = [...new Set(store.unmatchedWaMessages.map(m => m.phone_number))].length;
    const total = pendingCount + unmatchedPhones;
    badge.textContent = total;
    badge.style.display = total > 0 ? 'inline' : 'none';
}


// Window bridge
Object.assign(window, {
    renderWhatsAppSection,
    renderWhatsAppMessages,
    loadWhatsAppMessages,
    openWhatsAppPanel,
    closeWhatsAppPanel,
    updateWhatsAppBadge,
    loadUnmatchedOnInit,
    waCreateLead,
    waLinkToLead,
    waToggleConvo,
    waToggleSection,
    waShowAllMessages,
    _waFilterLeads,
    _waConfirmLink,
    waOpenQuoteModal,
    _waParseAndPreview,
    _waCreateFromQuote,
    waQuoteFromSearch,
    waInboxSearch,
    waTimeAgo: _timeAgo,
    checkColdConversations,
    autoLinkUnmatched,
    autoLinkSinglePhone,
    autoLinkForLead,
    renderWhatsAppInbox,
    setWaInboxTab,
    waInboxToggleConvo,
    updateWhatsAppInboxBadge,
    waMarkResponded,
});
